import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * @typedef {Object} PortalProps
 * @property {JSX.Element} children React component to inject in the existing DOM element
 * @property {string} containerId Existing DOM element ID
 * @property {boolean=} multiple Determine if multiple containers should be instantiated
 */

/**
 * Compose a component in existing DOM tree element through React Portal.
 * @param {PortalProps} props
 * @return {JSX.Element}
 */
const Portal = ({ children, containerId, multiple = false }) => {
  const findContainer = () => {
    if (multiple) {
      return document.querySelectorAll(`[id^="${containerId}"]`)
    }

    return document.getElementById(containerId)
  }

  const [container, setContainer] = useState(findContainer())

  /**
   * @type {React.ReactPortal[]}
   */
  const defaultValue = null
  const [portals, setPortals] = useState(defaultValue)

  const mountPortalComponent = () => {
    /**
     * @type {React.ReactPortal[]}
     */
    const portalInstance = []
    if (container instanceof NodeList) {
      container.forEach((containerElement) => {
        const el = document.createElement('div')
        containerElement.appendChild(el)
        portalInstance.push(createPortal(children, el))
        // console.log(`CreatingPortalFor:`, containerElement.id, ':', children)

        return () => {
          container.forEach((containerElement) => {
            containerElement.removeChild(el)
          })
        }
      })

      setPortals(portalInstance)
    }

    if (container instanceof HTMLElement) {
      const el = document.createElement('div')
      container.appendChild(el)
      portalInstance.push(createPortal(children, el))
      // console.log(`CreatingPortalFor:`, container.id, ':', children)

      setPortals(portalInstance)
      return () => container.removeChild(el)
    }

    return () => {}
  }

  useEffect(mountPortalComponent, [container])
  useEffect(() => {
    document.addEventListener('drupalajaxready', (event) => {
      if (!container) {
        setContainer(findContainer())
      }
    })
  }, [])

  if (!portals) {
    return null
  }

  return (
    <>
      {portals.map((portalInstance) => (
        <>{portalInstance}</>
      ))}
    </>
  )
}

export default Portal
